import {radialProgressiveBlur} from '@remotion/effects/radial-progressive-blur';
import {MacOSCursor} from '@remotion/mac-cursors';
import {Video} from '@remotion/media';
import {
	AbsoluteFill,
	Composition,
	Easing,
	HtmlInCanvas,
	Interactive,
	interpolate,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

const BasicsSourceOnlyTrimInner = ({
	captureStyle,
}: {
	readonly captureStyle: React.CSSProperties | null;
}) => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();

	const screenCapture = (
		<>
			<Video
				name="Screen recording"
				premountFor={fps}
				muted
				effects={
					HtmlInCanvas.isSupported()
						? []
						: [
								radialProgressiveBlur({
									center: [0.56, 0.64],
									width: 1.05,
									height: 0.68,
									start: 0.44,
									startBlur: 0,
									endBlur: 28,
								}),
							]
				}
				src={
					'https://remotion.media/studio-close-ups/basics-showcase/source-only-trim-920e7e395da4.mp4'
				}
				durationInFrames={375}
				style={{
					position: 'absolute',
				}}
			/>
			<MacOSCursor
				name="Recorded cursor"
				premountFor={fps}
				cursor={interpolate(frame, [12, 95, 250], ['auto', 'custom', 'auto'], {
					easing: Easing.step1,
					extrapolateLeft: 'clamp',
					extrapolateRight: 'clamp',
				})}
				customCursor={
					'url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2032%2032%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url(%23shadow)%22%3E%0A%3Cpath%20d%3D%22M3%2016L12%2010V22L3%2016Z%22%20fill%3D%22black%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M23%204.5H18C16.9%204.5%2016%205.4%2016%206.5V25.5C16%2026.6%2016.9%2027.5%2018%2027.5H23%22%20stroke%3D%22white%22%20stroke-width%3D%225%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M23%204.5H18C16.9%204.5%2016%205.4%2016%206.5V25.5C16%2026.6%2016.9%2027.5%2018%2027.5H23%22%20stroke%3D%22black%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22shadow%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2232%22%20height%3D%2232%22%20filterUnits%3D%22userSpaceOnUse%22%3E%0A%3CfeDropShadow%20dx%3D%220%22%20dy%3D%221.5%22%20stdDeviation%3D%221%22%20flood-color%3D%22black%22%20flood-opacity%3D%220.35%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E") 2 12, e-resize'
				}
				style={{
					position: 'absolute',
					left: 0,
					top: 0,
					scale: interpolate(
						frame,
						[0, 38, 43, 117, 236, 346, 351],
						[
							4.764021711036444, 4.287619539932799, 4.764021711036444,
							4.287619539932799, 4.764021711036444, 4.287619539932799,
							4.764021711036444,
						],
						{
							easing: Easing.step1,
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
					translate: interpolate(
						frame,
						[
							12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27,
							28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 57, 58, 59, 60, 61,
							62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77,
							78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93,
							94, 95, 96, 97, 98, 100, 101, 102, 103, 104, 105, 106, 107, 111,
							112, 146, 147, 148, 149, 150, 151, 152, 153, 154, 155, 156, 157,
							158, 159, 160, 161, 162, 163, 164, 165, 166, 167, 168, 169, 170,
							171, 172, 173, 174, 175, 176, 177, 178, 179, 180, 181, 182, 183,
							184, 185, 187, 188, 189, 190, 191, 192, 193, 194, 195, 196, 197,
							198, 199, 200, 201, 202, 203, 204, 206, 207, 209, 210, 211, 249,
							250, 251, 252, 253, 254, 255, 256, 257, 258, 259, 260, 261, 262,
							263, 264, 268, 269, 271, 272, 303, 304, 305, 306, 307, 308, 309,
							310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320, 321, 322,
							323, 324, 325, 326, 327, 328, 329, 330, 331, 332, 333, 334, 335,
							336, 337, 338, 339, 340, 341, 342, 343, 344, 345,
						],
						[
							'3213.5187386921684px 1207.846988886017px',
							'3167.8511243215303px 1207.846988886017px',
							'3094.604290514345px 1207.846988886017px',
							'2947.757043163608px 1184.3432411475833px',
							'2770.632204704058px 1150.660118893771px',
							'2674.1421555957613px 1124.3463427242182px',
							'2367.3652106487466px 1061.5021969501163px',
							'2255.1687774618763px 1040.1013181700698px',
							'2141.371930731455px 1021.1196691651589px',
							'2035.1677436029981px 998.6766606358232px',
							'1972.0444559317652px 982.8772292582063px',
							'1924.9811320754718px 973.7213750323081px',
							'1886.6456448694753px 960.6947531661929px',
							'1852.0134401654175px 950.4037218919617px',
							'1826.9464978030499px 939.4427500646162px',
							'1801.8795554406825px 929.1703282501938px',
							'1782.7490307573016px 919.5864564486948px',
							'1768.1592142672525px 913.8733522874128px',
							'1758.110105970535px 911.3610752132334px',
							'1754.4626518480227px 909.5373481519773px',
							'1751.131558542259px 908.0299819074696px',
							'1751.131558542259px 908.0299819074696px',
							'1749.7358490566037px 908.0299819074696px',
							'1749.7358490566037px 908.0299819074696px',
							'1748.4145774101835px 908.0299819074696px',
							'1748.4145774101835px 908.0299819074696px',
							'1747.7074179374515px 908.0299819074696px',
							'1747.7074179374515px 908.0299819074696px',
							'1746.5350219695013px 908.0299819074696px',
							'1708.1995347635047px 908.0299819074696px',
							'1662.5319203928664px 908.0299819074696px',
							'1616.864306022228px 908.0299819074696px',
							'1578.5288188162317px 908.0299819074696px',
							'1553.461876453864px 908.0299819074696px',
							'1534.3313517704833px 908.0299819074696px',
							'1519.7415352804342px 908.0299819074696px',
							'1509.6924269837168px 908.0299819074696px',
							'1497.819591625743px 908.0299819074696px',
							'1494.1721375032307px 908.0299819074696px',
							'1490.5246833807184px 908.0299819074696px',
							'1486.8772292582062px 908.0299819074696px',
							'1483.229775135694px 908.0299819074696px',
							'1479.5823210131816px 908.0299819074696px',
							'1476.251227707418px 908.0299819074696px',
							'1472.6037735849056px 908.0299819074696px',
							'1470.7800465236496px 908.0299819074696px',
							'1469.272680279142px 908.0299819074696px',
							'1465.9415869733782px 908.0299819074696px',
							'1464.4342207288705px 908.0299819074696px',
							'1462.9268544843628px 908.0299819074696px',
							'1461.4194882398554px 908.0299819074696px',
							'1460.0237787542px 908.0299819074696px',
							'1460.0237787542px 908.0299819074696px',
							'1458.6280692685448px 908.0299819074696px',
							'1457.1207030240373px 908.0299819074696px',
							'1455.6133367795296px 908.0299819074696px',
							'1452.282243473766px 908.0299819074696px',
							'1450.7748772292582px 908.0299819074696px',
							'1449.2675109847505px 908.0299819074696px',
							'1447.760144740243px 908.0299819074696px',
							'1445.9364176789868px 908.0299819074696px',
							'1444.429051434479px 908.0299819074696px',
							'1441.0979581287154px 908.0299819074696px',
							'1439.590591884208px 908.0299819074696px',
							'1439.590591884208px 908.0299819074696px',
							'1438.1948823985526px 908.0299819074696px',
							'1436.7991729128973px 908.0299819074696px',
							'1436.7991729128973px 908.0299819074696px',
							'1435.4779012664771px 908.0299819074696px',
							'1435.4779012664771px 908.0299819074696px',
							'1434.2124579994831px 908.0299819074696px',
							'1434.2124579994831px 908.0299819074696px',
							'1432.9097958128716px 908.0299819074696px',
							'1432.9097958128716px 908.0299819074696px',
							'1431.60713362626px 909.3140346342724px',
							'1431.60713362626px 909.3140346342724px',
							'1430.2858619798399px 909.3140346342724px',
							'1430.2858619798399px 909.3140346342724px',
							'1429.0576376324632px 909.3140346342724px',
							'1429.0576376324632px 909.3140346342724px',
							'1428.4807443783923px 909.3140346342724px',
							'1428.4807443783923px 909.3140346342724px',
							'1427.2711294908245px 909.3140346342724px',
							'1425.9126389247867px 909.3140346342724px',
							'1424.3866632204704px 909.3140346342724px',
							'1421.1486172137504px 909.3140346342724px',
							'1418.2269320237788px 909.3140346342724px',
							'1416.3287671232877px 909.3140346342724px',
							'1412.699922460584px 909.3140346342724px',
							'1412.699922460584px 909.3140346342724px',
							'1406.8379426208323px 909.3140346342724px',
							'1402.911346601189px 909.3140346342724px',
							'1397.2168518997157px 909.3140346342724px',
							'1393.4763504781597px 909.3140346342724px',
							'1389.7544585164126px 909.3140346342724px',
							'1385.9395192556217px 909.3140346342724px',
							'1384.4135435513053px 909.3140346342724px',
							'1383.0736624450763px 909.3140346342724px',
							'1381.39881106229px 909.3140346342724px',
							'1379.6309123804601px 909.3140346342724px',
							'1376.1509433962265px 909.3140346342724px',
							'1374.5877487722926px 909.3140346342724px',
							'1373.2106487464462px 909.3140346342724px',
							'1371.8149392607909px 909.3140346342724px',
							'1370.2703540966659px 909.3140346342724px',
							'1368.707159472732px 909.3140346342724px',
							'1368.707159472732px 909.3140346342724px',
							'1367.3114499870767px 909.3140346342724px',
							'1365.9901783406565px 909.3140346342724px',
							'1365.9901783406565px 909.3140346342724px',
							'1364.538640475575px 908.6254846213492px',
							'1363.0126647712586px 907.9183251486172px',
							'1361.6727836650298px 907.8811062289998px',
							'1360.3701214784182px 907.862496769191px',
							'1360.3701214784182px 907.862496769191px',
							'1358.8813646937192px 907.8438873093822px',
							'1357.411217368829px 907.8252778495736px',
							'1357.411217368829px 907.8252778495736px',
							'1356.0713362626002px 907.8066683897648px',
							'1356.0713362626002px 907.8066683897648px',
							'1354.7686740759887px 907.8066683897648px',
							'1354.7686740759887px 907.8066683897648px',
							'1353.5032308089947px 907.8066683897648px',
							'1353.5032308089947px 907.8066683897648px',
							'1352.2005686223831px 907.8066683897648px',
							'1352.2005686223831px 907.8066683897648px',
							'1350.7862496769192px 907.8066683897648px',
							'1350.7862496769192px 907.8066683897648px',
							'1349.4091496510725px 906.5412251227707px',
							'1349.4091496510725px 906.5412251227707px',
							'1348.0134401654175px 906.4481778237271px',
							'1348.0134401654175px 906.4481778237271px',
							'1346.6921685189973px 906.3923494443009px',
							'1346.6921685189973px 906.3923494443009px',
							'1345.4081157921944px 906.3737399844921px',
							'1345.4081157921944px 906.3737399844921px',
							'1344.012406306539px 906.3551305246834px',
							'1344.012406306539px 906.3551305246834px',
							'1342.691134660119px 906.3365210648747px',
							'1342.691134660119px 906.3365210648747px',
							'1342.0584130266218px 905.6851899715689px',
							'1341.425691393125px 905.0338588782631px',
							'1341.425691393125px 905.0338588782631px',
							'1341.425691393125px 918.0790902041871px',
							'1341.425691393125px 949.045231325924px',
							'1341.425691393125px 987.3434996123029px',
							'1341.425691393125px 1032.9738950633239px',
							'1341.425691393125px 1078.6042905143447px',
							'1341.425691393125px 1124.2346859653658px',
							'1341.425691393125px 1139.717756526234px',
							'1337.536314293099px 1169.7534246575342px',
							'1337.536314293099px 1181.5704316360816px',
							'1335.0240372189196px 1188.3814939260792px',
							'1335.0240372189196px 1191.6753683122254px',
							'1335.0240372189196px 1191.6753683122254px',
							'1335.0240372189196px 1192.978030498837px',
							'1333.516670974412px 1192.978030498837px',
							'1333.516670974412px 1194.4667872835357px',
							'1333.516670974412px 1194.4667872835357px',
							'1332.2884466270355px 1195.6764021711037px',
							'1332.2884466270355px 1195.6764021711037px',
							'1331.0416128198501px 1195.6764021711037px',
							'1331.0416128198501px 1195.6764021711037px',
							'1332.1953993279917px 1195.6764021711037px',
							'1347.6784698888603px 1195.6764021711037px',
							'1497.2054794520548px 1182.3520289480487px',
							'1658.4378392349445px 1166.1990178340657px',
							'1851.3807185319204px 1139.866632204704px',
							'2044.3235978288965px 1131.0829671749807px',
							'2205.555957611786px 1114.9299560609977px',
							'2391.0364435254587px 1085.433962264151px',
							'2455.5182217627294px 1079.4789351253553px',
							'2518.604290514345px 1068.9459808736108px',
							'2581.69035926596px 1058.413026621866px',
							'2627.3207547169814px 1053.835099508917px',
							'2672.951150168002px 1049.257172395968px',
							'2703.917291289739px 1041.4784181959162px',
							'2728.947014732489px 1038.2775911088136px',
							'2763.5233910571205px 1034.3882140087878px',
							'2794.4895321788576px 1030.4988369087619px',
							'2832.7878004652366px 1030.4988369087619px',
							'2871.0860687516156px 1030.4988369087619px',
							'2909.3843370379946px 1030.4988369087619px',
							'2947.682605324373px 1030.4988369087619px',
							'2978.64874644611px 1038.2403721891963px',
							'3013.225122770742px 1048.475575084001px',
							'3018.231067459292px 1050.9692426983718px',
							'3032.7836650297236px 1059.82734556733px',
							'3042.7955544068236px 1067.3269578702507px',
							'3057.8133884724734px 1074.8079607133627px',
							'3067.360041354355px 1081.1723959679505px',
							'3091.4592918066683px 1096.3949340914965px',
							'3106.942362367537px 1100.2657017317135px',
							'3126.0356681313px 1112.994572240889px',
							'3151.06539157405px 1127.1005427759112px',
							'3166.0832256397002px 1139.6060997673817px',
							'3166.0832256397002px 1139.6060997673817px',
							'3176.5045231325926px 1150.027397260274px',
							'3180.1147583354873px 1153.6376324631688px',
							'3181.9198759369347px 1157.2478676660635px',
							'3181.9198759369347px 1160.8581028689584px',
							'3183.724993538382px 1164.4683380718532px',
							'3183.724993538382px 1168.078573274748px',
							'3183.724993538382px 1169.8836908761955px',
							'3185.213750323081px 1173.1775652623417px',
						],
						{
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
				}}
			/>
		</>
	);

	return (
		<AbsoluteFill
			showInTimeline={false}
			style={{backgroundColor: '#111518', overflow: 'hidden'}}
		>
			{HtmlInCanvas.isSupported() ? (
				<HtmlInCanvas
					name="Canvas Capture"
					width={2496}
					height={1440}
					effects={[
						radialProgressiveBlur({
							center: [0.56, 0.64],
							width: 1.05,
							height: 0.68,
							start: 0.44,
							startBlur: 0,
							endBlur: 28,
						}),
					]}
					durationInFrames={375}
					premountFor={fps}
					style={{
						translate: '-720px -520px',
						scale: 1.15,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</HtmlInCanvas>
			) : (
				<AbsoluteFill
					name="Canvas Capture"
					durationInFrames={375}
					premountFor={fps}
					style={{
						width: 2496,
						height: 1440,
						translate: '-720px -520px',
						scale: 1.15,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</AbsoluteFill>
			)}
		</AbsoluteFill>
	);
};

export const BasicsSourceOnlyTrimPreview = Interactive.withSchema({
	Component: BasicsSourceOnlyTrimInner,
	componentName: 'BasicsSourceOnlyTrimPreview',
	schema: {},
	wrapInSequence: true,
	layout: 'absolute-fill',
});

export const BasicsSourceOnlyTrimComposition = () => {
	return (
		<Composition
			id="BasicsSourceOnlyTrim"
			component={BasicsSourceOnlyTrimPreview}
			width={1920}
			height={1080}
			defaultProps={{captureStyle: null}}
			fps={60}
			durationInFrames={375}
		/>
	);
};
